import { javascript } from "@codemirror/lang-javascript";
import { EditorView, basicSetup } from "codemirror";
import { useEffect, useRef } from "react";

/**
 * A JavaScript editor (CodeMirror 6; docs/phase5-plan.md §10 decision 2) for automation functions. Controlled loosely:
 * the editor owns its text while it's mounted and reports every change; a new `docKey` loads `value` afresh.
 */
export function CodeEditor({ value, onChange, docKey, readOnly, minHeight = "18rem", label }: { value: string; onChange?: (v: string) => void; docKey: string; readOnly?: boolean; minHeight?: string; label: string }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const changed = useRef(onChange);
  changed.current = onChange;
  useEffect(() => {
    const v = new EditorView({
      doc: value,
      parent: host.current!,
      extensions: [
        basicSetup,
        javascript(),
        EditorView.lineWrapping,
        EditorView.editable.of(!readOnly),
        EditorView.contentAttributes.of({ "aria-label": label }),
        EditorView.theme({ "&": { fontSize: "13px", minHeight }, ".cm-scroller": { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", minHeight } }),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) changed.current?.(u.state.doc.toString());
        }),
      ],
    });
    view.current = v;
    return () => v.destroy();
    // A new document (another function) rebuilds the editor; typing doesn't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey, readOnly]);
  return <div ref={host} className="rounded-lg border border-line bg-surface overflow-hidden" />;
}
